import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { subscriptionsAPI, abTestAPI } from '@/utils/api';
import { isAuthenticated } from '@/utils/auth';
import { toast } from 'sonner';
import { Check, ArrowLeft, Crown, Zap, Star } from 'lucide-react';
import { useLang } from '@/utils/i18n';
import LangSwitcher from '@/components/LangSwitcher';

const PricingPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(null);
  const [abVariant, setAbVariant] = useState('A');

  useEffect(() => {
    abTestAPI.getVariant('pricing_cta').then((res) => {
      if (res.data?.variant) setAbVariant(res.data.variant);
    }).catch(() => {});
  }, []);

  const pricingTiers = [
    { id: 'mini', name: 'Mini', price: 4.99, tips: 5, icon: null, extras: [] },
    { id: 'small', name: 'Small', price: 9.99, tips: 10, icon: null, extras: ['E-Mail Support'] },
    { id: 'medium', name: 'Medium', price: 19.99, tips: 20, icon: <Star className="w-5 h-5" />, featured: true, extras: ['E-Mail Support', 'Tipp des Tages Vollzugriff'] },
    { id: 'large', name: 'Large', price: 49.99, tips: 50, icon: <Zap className="w-5 h-5" />, extras: ['Priority Support', 'Tipp des Tages Vollzugriff', 'AI Chat Assistent'] },
    { id: 'xl', name: 'XL', price: 99.99, tips: 100, icon: <Zap className="w-5 h-5" />, extras: ['Priority Support', 'AI Chat Assistent', 'Erweiterte Analysen'] },
    { id: 'xxl', name: 'XXL', price: 499.99, tips: 500, icon: <Crown className="w-5 h-5" />, extras: ['VIP Support', 'AI Chat Assistent', 'Erweiterte Analysen', 'Exklusive Tipps'] },
    { id: 'unlimited', name: 'Unlimited', price: 999.99, tips: 'Unbegrenzt', icon: <Crown className="w-5 h-5" />, extras: ['VIP Support', 'AI Chat Assistent', 'Alle Features', 'Unbegrenzt'] },
  ];

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated()) {
      toast.error('Bitte zuerst anmelden');
      navigate('/auth');
      return;
    }
    setLoading(tier);
    try {
      // Track A/B test conversion
      abTestAPI.track('pricing_cta', 'checkout_click').catch(() => {});
      const response = await subscriptionsAPI.createCheckout(tier);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Checkout konnte nicht erstellt werden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Checkout');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="bg-[#1E1E1E] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-gray-400 hover:text-white"
            data-testid="pricing-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {t('choose_plan').split(' ').map((w, i) =>
              i === w.length - 1 ? <span key={i} className="text-[#00BFA6]">{w} </span> : w + ' '
            )}
          </h1>
          <p className="text-gray-400 text-lg">
            Monatlich kündbar. Bezahlung über PayPal. Keine versteckten Kosten.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
          {pricingTiers.map((tier) => (
            <Card
              key={tier.id}
              data-testid={`pricing-tier-${tier.id}`}
              className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                tier.featured
                  ? 'bg-gradient-to-b from-[#7B1FA2] to-[#4A148C] border-2 border-[#7B1FA2] shadow-lg shadow-[#7B1FA2]/20'
                  : 'bg-[#1E1E1E] border-white/10 hover:border-[#00BFA6]/50'
              }`}
            >
              <CardHeader className="pb-2">
                {tier.featured && (
                  <div className="text-xs font-bold text-[#00BFA6] mb-1 tracking-widest">{t('popular')}</div>
                )}
                <div className="flex items-center gap-2">
                  {tier.icon && <span className="text-[#00BFA6]">{tier.icon}</span>}
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-4xl font-bold font-mono mb-1">{tier.price}€</div>
                  <div className="text-gray-400 text-sm">{t('monthly')}</div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#00BFA6] shrink-0" />
                    <span className="font-bold">{tier.tips} {t('tips_per_month')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#00BFA6] shrink-0" />
                    <span>{t('multi_llm')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#00BFA6] shrink-0" />
                    <span>{t('detailed_analysis')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#00BFA6] shrink-0" />
                    <span>{t('push_notif')}</span>
                  </div>
                  {tier.extras.map((extra, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-yellow-400 shrink-0" />
                      <span className="text-yellow-200">{extra}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={loading === tier.id}
                  data-testid={`buy-${tier.id}`}
                  className={`w-full py-3 font-bold transition-all ${
                    tier.featured
                      ? 'bg-[#00BFA6] hover:bg-[#009E8A] text-black'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  {loading === tier.id ? t('loading') : (abVariant === 'B' ? 'Jetzt starten' : t('pay_paypal'))}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-gray-300 text-center">
            ⚠️ <strong>{t('legal_warning')}</strong> {t('help_at')}{' '}
            <a href="https://www.bzga.de" className="text-[#00BFA6] underline" target="_blank" rel="noopener noreferrer">
              www.bzga.de
            </a>
            <br />
            {t('adults_only')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
